/**
 * SetupGG — motor de extração e compatibilidade
 *
 * Extrai specs de compatibilidade (socket, tipo de RAM, form factor, wattage,
 * certificação 80 Plus) a partir do título + tags do produto, já que os
 * metafields de compatibilidade estão majoritariamente vazios nos produtos
 * mais recentes do catálogo (ver auditoria de 18/07/2026).
 *
 * Estratégia: título/tags são a fonte confiável (auditoria mostrou ~100% de
 * cobertura). Metafields, quando existirem e forem valores simples (não
 * referência a metaobjeto), servem só como checagem cruzada — não como
 * fonte primária.
 */

// ---------------------------------------------------------------------
// EXTRATORES — cada um recebe o produto (título + tags) e devolve o valor
// normalizado ou null se não encontrar.
// ---------------------------------------------------------------------

function productText(product) {
  // Junta título + tags num único texto pra rodar os regex uma vez só.
  const tags = Array.isArray(product.tags) ? product.tags.join(' ') : '';
  return `${product.title || ''} ${tags}`;
}

function extractSocket(product) {
  const text = productText(product);
  // Cobre: AM4, AM5, LGA1700, LGA 1700, LGA1851, LGA 1155 etc.
  const match = text.match(/\b(AM[45]|LGA\s?\d{3,4})\b/i);
  if (!match) return null;
  return match[1].toUpperCase().replace(/\s+/g, ''); // normaliza "LGA 1700" -> "LGA1700"
}

function extractRamType(product) {
  const text = productText(product);
  const match = text.match(/\bDDR([345])\b/i);
  return match ? `DDR${match[1]}` : null;
}

function extractMotherboardFormFactor(product) {
  const text = productText(product);
  // Ordem importa: "Micro ATX" precisa ser checado antes de "ATX" sozinho.
  if (/\bmini[\s-]?itx\b/i.test(text)) return 'Mini-ITX';
  if (/\bmicro[\s-]?atx\b|\bm[\s-]?atx\b/i.test(text)) return 'Micro-ATX';
  if (/\batx\b/i.test(text)) return 'ATX';
  return null;
}

// Hierarquia padrão de mercado: um gabinete rotulado com um tamanho suporta
// esse tamanho e todos os menores (ex: gabinete ATX aceita placa ATX,
// Micro-ATX e Mini-ITX; gabinete Mini-ITX só aceita Mini-ITX).
const FORM_FACTOR_HIERARCHY = ['ATX', 'Micro-ATX', 'Mini-ITX'];

/**
 * Extrai os form factors que o gabinete suporta, a partir do tamanho que o
 * próprio título/tags do gabinete anunciam (reaproveita o mesmo regex de
 * tamanho da placa-mãe) + hierarquia acima. Só lê título+tags — descrição
 * não é fonte, por decisão (título já é impreciso o suficiente às vezes;
 * descrição seria pior).
 */
function extractCaseFormFactors(product) {
  const size = extractMotherboardFormFactor(product);
  if (!size) return null;
  const idx = FORM_FACTOR_HIERARCHY.indexOf(size);
  return FORM_FACTOR_HIERARCHY.slice(idx);
}

function extractWattage(product) {
  const text = productText(product);
  const match = text.match(/\b(\d{3,4})\s?W\b/);
  return match ? parseInt(match[1], 10) : null;
}

function extractPsuFormFactor(product) {
  const text = productText(product);
  if (/\bsfx\b/i.test(text)) return 'SFX';
  if (/\batx\b/i.test(text)) return 'ATX';
  return null;
}

function extract80PlusRating(product) {
  const text = productText(product);
  const match = text.match(/80\s?Plus\s?(Titanium|Platinum|Gold|Bronze|White)/i);
  return match ? match[1] : null;
}

function extractPsuModularity(product) {
  const text = productText(product);
  if (/\bn[ãa]o\s?modular\b/i.test(text)) return 'Não Modular';
  if (/\bsemi\s?modular\b/i.test(text)) return 'Semi Modular';
  if (/\bmodular\b/i.test(text)) return 'Modular';
  return null;
}

function extractStorageInterface(product) {
  const text = productText(product);
  if (/\bnvme\b/i.test(text)) return 'NVMe';
  if (/\bsata\s?3?\b/i.test(text)) return 'SATA';
  if (/\bm\.2\b/i.test(text)) return 'M.2'; // pode coexistir com NVMe; checar NVMe primeiro
  return null;
}

/**
 * Estima o TDP (consumo) do processador a partir da linha/faixa do modelo —
 * não é dado estruturado (não existe no catálogo). Valores aproximados de
 * mercado, propositalmente conservadores (arredondados pra cima) pra dar
 * margem de segurança no cálculo de fonte recomendada.
 */
function extractCpuTdpEstimate(product) {
  const text = productText(product);
  if (/\bryzen\b/i.test(text)) {
    if (/\bryzen\s*9\b/i.test(text)) return 120;
    if (/\bryzen\s*7\b/i.test(text)) return 90;
    if (/\bryzen\s*5\b/i.test(text)) return 65;
    if (/\bryzen\s*3\b/i.test(text)) return 50;
    return 65;
  }
  if (/\bintel\b/i.test(text)) {
    if (/\b(i9|ultra\s*9)\b/i.test(text)) return 125;
    if (/\b(i7|ultra\s*7)\b/i.test(text)) return 100;
    if (/\b(i5|ultra\s*5)\b/i.test(text)) return 65;
    if (/\bi3\b/i.test(text)) return 60;
    return 65;
  }
  return 65; // marca não reconhecida, usa piso conservador
}

/**
 * Estima o TDP (consumo) da placa de vídeo a partir do modelo no título —
 * tabela com valores conhecidos de mercado das gerações mais comuns no
 * catálogo. Modelo não reconhecido cai num valor médio conservador (250W).
 */
const GPU_TDP_TABLE = {
  // NVIDIA RTX 50
  '5090': 575, '5080': 360, '5070ti': 300, '5070': 250, '5060ti': 180, '5060': 145,
  // NVIDIA RTX 40
  '4090': 450, '4080': 320, '4070ti': 285, '4070': 200, '4060ti': 160, '4060': 115,
  // NVIDIA RTX 30
  '3090': 350, '3080': 320, '3070ti': 290, '3070': 220, '3060ti': 200, '3060': 170,
  // NVIDIA GTX (geração anterior, ainda comum em catálogo de entrada)
  '1660': 120, '1650': 75,
  // AMD RX 7000
  '7900xtx': 355, '7900xt': 315, '7800xt': 263, '7700xt': 245, '7600': 165,
  // AMD RX 6000
  '6900xt': 300, '6800xt': 300, '6700xt': 230, '6600': 132, '6500xt': 107,
};

// Chaves numéricas puras (ex: '4070') são reordenadas pelo JS antes das que
// têm letra (ex: '4070ti'), então a ordem de inserção do objeto não garante
// que o sufixo mais específico seja checado primeiro — por isso ordenamos
// explicitamente por tamanho de string decrescente antes de buscar.
const GPU_TDP_KEYS_BY_SPECIFICITY = Object.keys(GPU_TDP_TABLE).sort((a, b) => b.length - a.length);

function extractGpuTdpEstimate(product) {
  const text = productText(product).toLowerCase().replace(/\s+/g, '');
  for (const key of GPU_TDP_KEYS_BY_SPECIFICITY) {
    if (text.indexOf(key) !== -1) return GPU_TDP_TABLE[key];
  }
  return 250; // modelo não reconhecido, estimativa média conservadora
}
/**
 * Estima se o processador tem vídeo integrado, a partir do padrão de nome do
 * modelo — não é dado estruturado (não existe metafield/campo pra isso no
 * catálogo). Convenção de mercado:
 *   - Intel: sufixo "F" ou "KF" no modelo = SEM vídeo integrado. Sem esse
 *     sufixo, a grande maioria dos Intel modernos TEM vídeo integrado.
 *   - AMD Ryzen: sufixo "G" no modelo = APU, COM vídeo integrado. Sem esse
 *     sufixo, a grande maioria dos Ryzen de desktop NÃO tem vídeo integrado.
 * Retorna true/false quando dá pra inferir, ou null se não reconhecer o padrão.
 */
function extractHasIntegratedGraphics(product) {
  const text = productText(product);
  const isIntel = /\bintel\b/i.test(text);
  const isAmdRyzen = /\bryzen\b/i.test(text);

  if (isIntel) {
    // Ex: i5-12400F, i7-13700KF -> sem vídeo integrado
    if (/\bi[3579][\s-]?\d{4,5}k?f\b/i.test(text)) return false;
    return true;
  }
  if (isAmdRyzen) {
    // Ex: Ryzen 5 5600G, Ryzen 7 8700G -> APU, com vídeo integrado
    if (/\bryzen\s*\d\s*\d{3,4}g\b/i.test(text)) return true;
    return false;
  }
  return null; // marca/padrão não reconhecido, não dá pra afirmar
}

// ---------------------------------------------------------------------
// CATEGORIZAÇÃO — mapeia productType (Shopify) -> etapa do configurador
// ---------------------------------------------------------------------

const CATEGORY_MAP = {
  'Placa Mãe': 'motherboard',
  'Processador': 'cpu',
  'Memória RAM': 'ram',
  'SSD Nvme': 'storage',
  'SSD': 'storage',
  'Placa de Vídeo': 'gpu',
  'Fonte de Alimentação': 'psu',
  'Gabinete': 'case',
  'Cooler': 'cooler',
  'Water Cooler': 'cooler',
  'Watercooler': 'cooler',
  'Cooler Líquido': 'cooler',
  'Pasta Térmica': 'cooler',
  'Periférico': 'peripherals',
  'Mouse': 'peripherals',
  'Teclado': 'peripherals',
  'Headset': 'peripherals',
  'Monitor': 'peripherals',
  'Caixa de Som': 'peripherals',
  'Webcam': 'peripherals',
};

function categorize(product) {
  return CATEGORY_MAP[product.productType] || null;
}

// ---------------------------------------------------------------------
// ENRIQUECIMENTO — adiciona os campos derivados a um produto
// ---------------------------------------------------------------------

function enrichProduct(product) {
  const category = categorize(product);
  const derived = {};

  switch (category) {
    case 'motherboard':
      derived.socket = extractSocket(product);
      derived.ramType = extractRamType(product);
      derived.formFactor = extractMotherboardFormFactor(product);
      break;
    case 'cpu':
      derived.socket = extractSocket(product);
      derived.hasIntegratedGraphics = extractHasIntegratedGraphics(product);
      derived.tdpEstimate = extractCpuTdpEstimate(product);
      break;
    case 'ram':
      derived.ramType = extractRamType(product);
      break;
    case 'psu':
      derived.wattage = extractWattage(product);
      derived.formFactor = extractPsuFormFactor(product);
      derived.plusRating = extract80PlusRating(product);
      derived.modularity = extractPsuModularity(product);
      break;
    case 'storage':
      derived.interface = extractStorageInterface(product);
      break;
    case 'gpu':
      derived.tdpEstimate = extractGpuTdpEstimate(product);
      break;
    case 'case':
      derived.supportedFormFactors = extractCaseFormFactors(product);
      break;
    case 'cooler':
      // Reaproveita o mesmo extrator de socket de CPU/placa-mãe — coolers
      // (ar ou water cooler/AIO) costumam listar sockets suportados no
      // título/tags (ex: "AM4/AM5/LGA1700"). Se o produto listar vários
      // sockets, extractSocket pega só o primeiro encontrado no texto —
      // suficiente pra checagem de compatibilidade básica.
      derived.socket = extractSocket(product);
      break;
    case 'peripherals':
      // Sem regra de compatibilidade — mouse/teclado/headset/monitor não
      // dependem de nenhuma outra peça da build.
      break;
  }

  return { ...product, category, derived };
}

// ---------------------------------------------------------------------
// COMPATIBILIDADE — dado o que já foi escolhido, filtra a próxima etapa
// ---------------------------------------------------------------------

/**
 * @param {object} build - peças já escolhidas, chaveadas por categoria.
 *   Ex: { motherboard: enrichedProduct }
 * @param {object[]} candidates - lista de produtos (já enriquecidos) da
 *   próxima categoria a filtrar.
 * @param {string} targetCategory - categoria dos candidates.
 */
/**
 * Compara o socket do candidato com o da placa-mãe escolhida.
 * strictWhenMissing=true (CPU): esconde o candidato se ele não menciona
 * socket no título — CPU sem socket detectável é dado ruim demais pra
 * arriscar mostrar como compatível.
 * strictWhenMissing=false (cooler): mostra o candidato mesmo sem socket
 * detectável — coolers "universais" raramente listam socket no título,
 * e esconder demais nessa etapa é pior que mostrar um pouco a mais.
 */
function socketCompatible(build, product, strictWhenMissing) {
  const mb = build.motherboard;
  if (!mb || !mb.derived.socket) return true; // sem placa-mãe escolhida ainda, mostra tudo
  if (!product.derived.socket) return !strictWhenMissing;
  return product.derived.socket === mb.derived.socket;
}

function filterCompatible(build, candidates, targetCategory) {
  return candidates.filter((product) => {
    if (product.category !== targetCategory) return false;

    switch (targetCategory) {
      case 'cpu':
        return socketCompatible(build, product, true);
      case 'ram': {
        const mb = build.motherboard;
        if (!mb || !mb.derived.ramType) return true;
        return product.derived.ramType === mb.derived.ramType;
      }
      case 'psu': {
        // Filtro de elegibilidade continua simples: só exige wattage
        // cadastrado. O cálculo de wattage recomendado (TDP estimado de
        // CPU + GPU + margem) já existe e roda como aviso não-bloqueante
        // no setupgg.liquid (recommendedWattage()) — de propósito não
        // filtra aqui, já que o TDP é estimado por regex no nome do
        // produto, não é dado real de fabricante.
        return product.derived.wattage != null;
      }
      case 'cooler':
        return socketCompatible(build, product, false);
      case 'case': {
        const mb = build.motherboard;
        if (!mb || !mb.derived.formFactor) return true; // sem placa-mãe escolhida, mostra tudo
        if (!product.derived.supportedFormFactors) return true; // gabinete sem tamanho detectável, não bloqueia (mesma filosofia do cooler "universal")
        return product.derived.supportedFormFactors.indexOf(mb.derived.formFactor) !== -1;
      }
      default:
        return true;
    }
  });
}

// ---------------------------------------------------------------------
// AUTOTESTE — roda contra títulos reais do catálogo (auditoria 18/07/2026)
// pra validar que os regex batem antes de plugar na loja de verdade.
// ---------------------------------------------------------------------

function runSelfTest() {
  const samples = [
    { title: 'Placa Mãe ASUS Prime B850M-A Socket AM5 / DDR5', tags: ['AM5', 'ASUS', 'DDR5', 'Micro ATX'], productType: 'Placa Mãe' },
    { title: 'Placa Mãe MSI Pro H810M-E Socket LGA 1851 / DDR5', tags: ['DDR5', 'Micro ATX', 'MSI', 'Placa Mãe'], productType: 'Placa Mãe' },
    { title: 'Placa Mãe Macrovip MV-B75V2 Socket LGA 1155 / VGA / DDR3', tags: ['DDR3', 'MacroVip', 'Placa Mãe'], productType: 'Placa Mãe' },
    { title: 'Placa Mãe ASRock B850 Steel Legend Wi-Fi Socket AM5 / DDR5', tags: ['AM5', 'ASRock', 'ATX', 'DDR5'], productType: 'Placa Mãe' },
    { title: 'Processador AMD Ryzen 5 9600X Socket AM5 / 5.4GHz / 38MB', tags: ['3.9 GHz', 'AM5', 'AMD', 'DDR5'], productType: 'Processador' },
    { title: 'Processador Intel Core Ultra 5 235 Socket LGA 1851 / 24MB', tags: ['INTEL', 'Processador'], productType: 'Processador' },
    { title: 'Processador Intel Core i5 12400F Socket LGA 1700 / 2.5GHz', tags: ['INTEL', 'Processador'], productType: 'Processador' },
    { title: 'Processador AMD Ryzen 5 5600G Socket AM4 / com vídeo integrado', tags: ['AMD', 'AM4', 'Processador'], productType: 'Processador' },
    { title: 'Memória RAM Kingston Fury Beast DDR4 16GB 3200MHz RGB', tags: ['16GB', '3200MHz', 'DDR4', 'Kingston', 'RGB'], productType: 'Memória RAM' },
    { title: 'Memória RAM ADATA XPG Lancer Blade DDR5 16GB 5600MHz - Preto', tags: ['16GB', '5600MHz', 'Adata', 'DDR5'], productType: 'Memória RAM' },
    { title: 'Fonte de Alimentação Corsair CX750 750W ATX / Não Modular / 80 Plus Bronze', tags: ['750W', '80 Plus Bronze', 'ATX', 'Não Modular'], productType: 'Fonte de Alimentação' },
    { title: 'Fonte de Alimentação Redragon GC-PS014W 650W SFX / Modular / 80 Plus Gold - Branco', tags: ['650W', '80 Plus Gold'], productType: 'Fonte de Alimentação' },
    { title: 'SSD Crucial M.2 480GB E100 NVMe', tags: ['480GB', 'M.2', 'NVMe'], productType: 'SSD Nvme' },
    { title: 'SSD Lexar 256GB NQ100 2.5" SATA 3', tags: ['256GB', 'LEXAR', 'SATA 3', 'SSD'], productType: 'SSD' },
    { title: 'Placa de Vídeo RTX 4070 Ti 12GB GDDR6X', tags: ['RTX', 'NVIDIA'], productType: 'Placa de Vídeo' },
    { title: 'Placa de Vídeo RX 7800 XT 16GB', tags: ['RX', 'AMD'], productType: 'Placa de Vídeo' },
  ];

  console.log('--- Autoteste do motor de compatibilidade ---');
  samples.forEach((s) => {
    const enriched = enrichProduct(s);
    console.log(`[${enriched.category}] ${s.title}`);
    console.log('  derived:', enriched.derived);
  });
}

// Descomente pra rodar localmente com node:
// runSelfTest();

const CompatEngine = {
  extractSocket,
  extractRamType,
  extractMotherboardFormFactor,
  extractCaseFormFactors,
  extractWattage,
  extractPsuFormFactor,
  extract80PlusRating,
  extractPsuModularity,
  extractStorageInterface,
  extractHasIntegratedGraphics,
  extractCpuTdpEstimate,
  extractGpuTdpEstimate,
  categorize,
  enrichProduct,
  filterCompatible,
  runSelfTest,
  CATEGORY_MAP,
};

// UMD simples: exporta via module.exports no Node (autoteste),
// ou pendura em window.CompatEngine no navegador (tema Shopify).
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CompatEngine;
} else if (typeof window !== 'undefined') {
  window.CompatEngine = CompatEngine;
}
